import { motion } from "framer-motion";
import { useLang } from "../context/LanguageContext";
import { copy } from "../data/copy";

const chapterImages = [
    "/img/sun.jpg",
    "/img/oil.jpg",
    "/img/hands.jpg",
];

export const Manifesto = () => {
    const { t, lang } = useLang();

    return (
        <section
            id="story"
            className="relative py-24 md:py-40 bg-[color:var(--kp-bone)]"
            data-testid="manifesto-section"
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-10">
                {/* Heading row */}
                <div className="grid grid-cols-12 gap-4 md:gap-8 mb-16 md:mb-24 items-start">
                    <div className="col-span-12 md:col-span-4">
                        <div className="flex items-center gap-3 mb-4">
                            <span className="w-8 h-px bg-[color:var(--kp-red)]" />
                            <span
                                className={`text-[11px] tracking-mono-wide text-[color:var(--kp-ink)]/60 ${
                                    lang === "te" ? "font-telugu-body" : ""
                                }`}
                            >
                                {t(copy.manifesto.eyebrow)}
                            </span>
                        </div>
                    </div>
                    <div className="col-span-12 md:col-span-8">
                        <motion.h2
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
                            className={`text-[9vw] md:text-[6vw] leading-[0.95] tracking-editorial text-[color:var(--kp-ink)] whitespace-pre-line ${
                                lang === "te"
                                    ? "font-telugu-display"
                                    : "font-serif-display"
                            }`}
                            data-testid="manifesto-title"
                        >
                            {t(copy.manifesto.title)
                                .split("\n")
                                .map((line, i) => (
                                    <span key={i} className="block">
                                        {i === 1 ? (
                                            <em className="italic text-[color:var(--kp-red)]">
                                                {line}
                                            </em>
                                        ) : (
                                            line
                                        )}
                                    </span>
                                ))}
                        </motion.h2>
                    </div>
                </div>

                {/* Chapters */}
                <div className="flex flex-col divide-y divide-[color:var(--kp-ink)]/15">
                    {copy.manifesto.chapters.map((ch, i) => {
                        const reversed = i % 2 === 1;
                        return (
                            <motion.div
                                key={ch.no}
                                initial={{ opacity: 0, y: 40 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{
                                    duration: 0.9,
                                    delay: 0.05 * i,
                                    ease: [0.19, 1, 0.22, 1],
                                }}
                                className={`grid grid-cols-12 gap-4 md:gap-8 py-14 md:py-20 items-center ${
                                    reversed ? "md:flex-row-reverse" : ""
                                }`}
                                data-testid={`chapter-${ch.no}`}
                            >
                                {/* Number */}
                                <div className="col-span-2 md:col-span-1">
                                    <div className="font-serif-display italic text-3xl md:text-4xl text-[color:var(--kp-red)]">
                                        {ch.no}
                                    </div>
                                </div>

                                {/* Text */}
                                <div
                                    className={`col-span-10 md:col-span-5 ${
                                        reversed ? "md:order-3" : ""
                                    }`}
                                >
                                    <h3
                                        className={`text-4xl md:text-5xl mb-4 tracking-editorial text-[color:var(--kp-ink)] ${
                                            lang === "te"
                                                ? "font-telugu-display"
                                                : "font-serif-display"
                                        }`}
                                    >
                                        {t(ch.title)}
                                    </h3>
                                    <p
                                        className={`text-base md:text-lg text-[color:var(--kp-ink)]/75 leading-relaxed max-w-md ${
                                            lang === "te" ? "font-telugu-body" : ""
                                        }`}
                                    >
                                        {t(ch.body)}
                                    </p>
                                </div>

                                {/* Image */}
                                <div
                                    className={`col-span-12 md:col-span-6 ${
                                        reversed ? "md:order-2" : ""
                                    }`}
                                >
                                    <div className="clipped-frame aspect-[4/3] md:aspect-[5/4]">
                                        <img
                                            src={chapterImages[i]}
                                            alt=""
                                            className="absolute inset-0 w-full h-full object-cover"
                                            style={{ filter: "saturate(1.05)" }}
                                        />
                                        <div className="absolute inset-0 ring-1 ring-inset ring-[color:var(--kp-ink)]/20" />
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};
